<template>
  <div>
    <div class="row mb-3">
      <div class="col-12 d-flex justify-content-end">
        <button
          type="button"
          class="btn btn-primary"
          @click="openRegisterModal"
        >
          Nueva Solicitud
        </button>
      </div>
    </div>
    <div class="row mb-3">
      <div class="col-md-4">
        <select class="form-select" v-model="actionFilter">
          <option value="">Todos los tipos</option>
          <option value="COMPROBANTE">Comprobante</option>
          <option value="CERTIFICADO">Certificado</option>
          <option value="CONTRASEÑA">Restablecer Contraseña</option>
        </select>
      </div>
      <div class="col-md-8">
        <input
          type="text"
          class="form-control"
          v-model="searchText"
          placeholder="Buscar por documento, evento o usuario..."
        />
      </div>
    </div>
    <div class="row">
      <div class="col-12">
        <div class="table-responsive">
          <table class="table">
            <thead>
              <tr>
                <th class="text-center">Nro</th>
                <th class="text-center">Tipo</th>
                <th class="text-center">Documento</th>
                <th class="text-center">Fecha</th>
                <th class="text-center">Acciones</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="loading">
                <td colspan="6" class="text-center">
                  <i class="fa fa-spinner fa-spin"></i> Cargando...
                </td>
              </tr>
              <tr v-else-if="paginatedItems.length === 0">
                <td colspan="6" class="text-center">
                  <EmptyState
                    title="No hay solicitudes registradas."
                    :size="450"
                    fontSize="fs-5"
                  />
                </td>
              </tr>
              <tr
                v-else
                v-for="(ticket, index) in paginatedItems"
                :key="ticket.id"
              >
                <td class="text-center">
                  {{ (currentPage - 1) * itemsPerPage + index + 1 }}
                </td>
                <td class="text-center">
                  <span class="badge" :class="actionBadgeClass(ticket.action)">
                    {{ actionText(ticket.action) }}
                  </span>
                </td>
                <td class="text-center">{{ ticket.participant_document }}</td>
                <td class="text-center">{{ formatDate(ticket.created_at) }}</td>
                <td class="text-center">
                  <div class="btn-group" role="group">
                    <button
                      type="button"
                      class="btn btn-sm btn-info"
                      @click="viewDetail(ticket)"
                      title="Ver detalle"
                    >
                      <i class="fa fa-eye"></i>
                    </button>
                    <button
                      type="button"
                      class="btn btn-sm btn-success"
                      @click="completeTicket(ticket)"
                      :disabled="processing[ticket.id]"
                      title="Completar solicitud"
                    >
                      <i
                        :class="
                          processing[ticket.id]
                            ? 'fa fa-spinner fa-spin'
                            : 'fa fa-check'
                        "
                      ></i>
                    </button>
                    <button
                      type="button"
                      class="btn btn-sm btn-danger"
                      @click="cancelTicket(ticket)"
                      :disabled="processing[ticket.id]"
                      title="Cancelar"
                    >
                      <i class="fa fa-times"></i>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <Pagination
          v-if="filteredList.length > itemsPerPage"
          :current-page="currentPage"
          :total-items="filteredList.length"
          :items-per-page="itemsPerPage"
          @page-changed="handlePageChange"
        />
      </div>
    </div>
    <RegisterModal ref="registerModal" @ticket-created="onTicketCreated" />
    <DetailModal ref="detailModal" @ticket-updated="onTicketUpdated" />
  </div>
</template>

<script setup>
import { ref, computed, watch, inject } from "vue";
import axios from "@/includes/connection";
import Pagination from "@/pages/components/Pagination.vue";
import RegisterModal from "./RegisterModal.vue";
import DetailModal from "./DetailModal.vue";
import EmptyState from "@/components/emptyState.vue";
import { toast } from "vue3-toastify";

const props = defineProps({
  tickets: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["ticket-created", "ticket-updated"]);
const swal = inject("$swal");

const registerModal = ref(null);
const detailModal = ref(null);
const actionFilter = ref("");
const searchText = ref("");
const currentPage = ref(1);
const itemsPerPage = ref(10);
const processing = ref({});

const filteredList = computed(() => {
  let filtered = [...props.tickets];

  if (actionFilter.value) {
    filtered = filtered.filter(
      (ticket) => ticket.action === actionFilter.value
    );
  }

  if (searchText.value) {
    const search = searchText.value.toLowerCase();
    filtered = filtered.filter(
      (ticket) =>
        ticket.participant_document.toLowerCase().includes(search) ||
        ticket.event?.title.toLowerCase().includes(search) ||
        ticket.user?.name.toLowerCase().includes(search)
    );
  }

  return filtered;
});

const paginatedItems = computed(() => {
  const startIndex = (currentPage.value - 1) * itemsPerPage.value;
  const endIndex = startIndex + itemsPerPage.value;
  return filteredList.value.slice(startIndex, endIndex);
});

watch([actionFilter, searchText], () => {
  currentPage.value = 1;
});

function handlePageChange(page) {
  currentPage.value = page;
}

function openRegisterModal() {
  registerModal.value.show();
}

function viewDetail(ticket) {
  detailModal.value.show(ticket);
}

function onTicketCreated(newTicket) {
  emit("ticket-created", newTicket);
  toast.success("Solicitud creada exitosamente");
}

function onTicketUpdated(updatedTicket) {
  emit("ticket-updated", updatedTicket);
}

async function cancelTicket(ticket) {
  const result = await swal.fire({
    title: "¿Cancelar solicitud?",
    text: "Esta acción no se puede deshacer",
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#d33",
    cancelButtonColor: "#3085d6",
    confirmButtonText: "Sí, cancelar",
    cancelButtonText: "No",
  });

  if (!result.isConfirmed) return;

  try {
    const response = await axios.patch(`/ticket/cancel/${ticket.id}`, {
      status: "CANCELADO",
    });

    if (response.data) {
      emit("ticket-updated", response.data);
      toast.success("Solicitud cancelada");
    }
  } catch (error) {
    console.error("Error", error);
    toast.error("Error al cancelar la solicitud");
  }
}

async function completeTicket(ticket) {
  const actionTexts = {
    COMPROBANTE: "generar el comprobante",
    CERTIFICADO: "generar el certificado",
    CONTRASEÑA: "restablecer la contraseña",
  };

  const result = await swal.fire({
    title: "¿Completar solicitud?",
    text: `Se procederá a ${
      actionTexts[ticket.action] || "completar la acción"
    }`,
    icon: "question",
    showCancelButton: true,
    confirmButtonColor: "#3085d6",
    cancelButtonColor: "#d33",
    confirmButtonText: "Sí, continuar",
    cancelButtonText: "Cancelar",
  });

  if (!result.isConfirmed) return;

  processing.value[ticket.id] = true;

  try {
    let response;

    switch (ticket.action) {
      case "COMPROBANTE":
        response = await comprobant(ticket);
        break;
      case "CERTIFICADO":
        response = await certificate(ticket);
        break;
      case "CONTRASEÑA":
        response = await rPassword(ticket);
        break;
      default:
        throw new Error("Acción no válida");
    }

    if (response) {
      const completeResponse = await axios.patch(
        `/ticket/complete/${ticket.id}`
      );
      if (completeResponse.data) {
        emit("ticket-updated", completeResponse.data);
        toast.success("Solicitud completada exitosamente");
      }
    }
  } catch (error) {
    console.error("Error", error);
    toast.error(
      error.response?.data?.message || "Error al completar la solicitud"
    );
  } finally {
    delete processing.value[ticket.id];
  }
}

async function comprobant(ticket) {
  try {
    const { generateComprobant } = await import("@/auth/components/comprobant");
    const result = await axios.get(
      `/event-enrollment/comprobant/${ticket.event_enrollment_id}`
    );
    await generateComprobant(result.data);
    return true;
  } catch (error) {
    console.error("Error generando comprobante", error);
    throw error;
  }
}

async function certificate(ticket) {
  try {
    const { generateCertificate } = await import(
      "@/auth/components/certificate"
    );
    const info = {
      eventId: ticket.event_id,
      userId: ticket.event_enrollment_id,
    };
    const response = await axios.post(`/certification/info/by-user/`, info);
    await generateCertificate(response.data);
    return true;
  } catch (error) {
    console.error("Error generando certificado", error);
    throw error;
  }
}

async function rPassword(ticket) {
  try {
    await axios.post(`/clie/reset-pass`, {
      document: ticket.participant_document,
      user_type: ticket.user_type,
    });
    return true;
  } catch (error) {
    console.error("Error restableciendo contraseña", error);
    throw error;
  }
}

function actionText(action) {
  const texts = {
    COMPROBANTE: "Comprobante",
    CERTIFICADO: "Certificado",
    CONTRASEÑA: "Contraseña",
  };
  return texts[action] || action;
}

function actionBadgeClass(action) {
  const classes = {
    COMPROBANTE: "bg-primary",
    CERTIFICADO: "bg-success",
    CONTRASEÑA: "bg-warning",
  };
  return classes[action] || "bg-secondary";
}

function formatDate(dateString) {
  if (!dateString) return "";
  return new Date(dateString).toLocaleDateString("es-ES", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}
</script>
